import type { Profile, Clinic, AuthSession, UserRole } from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';

/**
 * Serviço de autenticação — Repository Pattern
 */
export class AuthService {
  /**
   * Inicia sessão com email e password
   */
  async signIn(email: string, password: string): Promise<AuthSession> {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data.user) throw new Error('Utilizador não encontrado');

    await this.linkClientProfileIfNeeded();

    const session = await this.getSession();
    if (!session) throw new Error('Sessão não encontrada');

    return session;
  }

  /**
   * Registo de cliente final
   * @throws Error com mensagem amigável ou 'EMAIL_CONFIRMATION_REQUIRED'
   */
  async signUp(email: string, password: string, fullName: string): Promise<AuthSession> {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });

    if (error) {
      if (error.message.toLowerCase().includes('already registered')) {
        throw new Error('Este email já está registado. Tente entrar.');
      }
      throw error;
    }

    if (!data.user) throw new Error('Registo falhou');

    if (data.user.identities?.length === 0) {
      throw new Error('Este email já está registado. Tente entrar.');
    }

    if (!data.session) {
      throw new Error('EMAIL_CONFIRMATION_REQUIRED');
    }

    await this.linkClientProfileIfNeeded();

    const session = await this.getSession();
    if (!session) throw new Error('Sessão não encontrada');

    return session;
  }

  /**
   * Liga perfil auth ao registo de cliente existente (por email)
   */
  async linkClientProfileIfNeeded(): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc('link_client_to_profile');
    if (error && !error.message.includes('Could not find the function')) {
      throw error;
    }
  }

  /**
   * Termina sessão
   */
  async signOut(): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  /**
   * Obtém sessão atual com perfil e clínica
   */
  async getSession(): Promise<AuthSession | null> {
    const supabase = getSupabaseClient();

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (profileError || !profile) return null;

    let clinic: Clinic | null = null;
    if (profile.clinic_id) {
      const { data: clinicData } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', profile.clinic_id)
        .single();
      clinic = clinicData as Clinic | null;
    }

    return {
      user: profile as Profile,
      clinic,
    };
  }

  /**
   * Envia email de recuperação de password
   */
  async resetPassword(email: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }

  /**
   * Atualiza password do utilizador
   */
  async updatePassword(password: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  /**
   * Reenvia email de confirmação de registo
   */
  async resendSignupConfirmation(email: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    if (error) throw error;
  }

  /**
   * Mensagem amigável para erros de autenticação
   */
  getAuthErrorMessage(error: unknown): string {
    if (!(error instanceof Error)) return 'Ocorreu um erro. Tente novamente.';
    const msg = error.message.toLowerCase();
    if (msg.includes('email not confirmed')) {
      return 'EMAIL_NOT_CONFIRMED';
    }
    if (msg.includes('rate limit') || msg.includes('email rate limit')) {
      return 'Limite de emails do Supabase. Aguarde ~1 hora ou configure SMTP customizado.';
    }
    if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
      return 'Email ou password incorrectos.';
    }
    if (msg.includes('already registered')) {
      return 'Este email já está registado. Tente entrar.';
    }
    return error.message;
  }

  /**
   * Verifica se o utilizador tem um dos papéis permitidos
   */
  hasRole(user: Profile, roles: UserRole[]): boolean {
    return roles.includes(user.role);
  }
}

export const authService = new AuthService();
